import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  EyeOff,
  X,
  Columns3
} from "lucide-react";
import { CreateJobButton } from "@/components/job/CreateJobButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type FilterType = "all" | "owned" | "shared" | "active" | "inactive" | "published" | "draft";

interface TestToolbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  activeFilters: FilterType[];
  onFilterChange: (filters: FilterType[]) => void;
  visibleColumns: string[];
  onColumnVisibilityChange: (columns: string[]) => void;
  totalCount: number;
  filteredCount: number;
}

const filterOptions: { value: FilterType; label: string; color?: string }[] = [
  { value: "all", label: "All Tests" },
  { value: "owned", label: "Owned by Me", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "shared", label: "Shared with Me", color: "bg-orange-50 text-orange-700 border-orange-200" },
  { value: "active", label: "Active", color: "bg-green-50 text-green-700 border-green-200" },
  { value: "inactive", label: "Inactive", color: "bg-gray-50 text-gray-700 border-gray-200" },
  { value: "published", label: "Published", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "draft", label: "Draft", color: "bg-yellow-50 text-yellow-700 border-yellow-200" }
];

const columnOptions = [
  { value: "title", label: "Test Name" },
  { value: "responses", label: "Responses" },
  { value: "location", label: "Location" },
  { value: "created", label: "Created Date" },
  { value: "active", label: "Active State" },
  { value: "actions", label: "Actions" }
];

export const TestToolbar = ({
  searchQuery,
  onSearchChange,
  activeFilters,
  onFilterChange,
  visibleColumns,
  onColumnVisibilityChange,
  totalCount,
  filteredCount
}: TestToolbarProps) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const handleFilterToggle = useCallback((filter: FilterType) => {
    if (filter === "all") {
      onFilterChange([]);
      return;
    }

    const newFilters = activeFilters.includes(filter)
      ? activeFilters.filter(f => f !== filter)
      : [...activeFilters.filter(f => f !== "all"), filter];
    
    onFilterChange(newFilters);
  }, [activeFilters, onFilterChange]);

  const handleRemoveFilter = useCallback((filter: FilterType) => {
    onFilterChange(activeFilters.filter(f => f !== filter));
  }, [activeFilters, onFilterChange]);

  const handleClearAllFilters = useCallback(() => {
    onFilterChange([]);
  }, [onFilterChange]);

  const handleColumnToggle = useCallback((column: string) => {
    const newColumns = visibleColumns.includes(column)
      ? visibleColumns.filter(c => c !== column)
      : [...visibleColumns, column];
    onColumnVisibilityChange(newColumns);
  }, [visibleColumns, onColumnVisibilityChange]);

  return (
    <div className="space-y-4">
      {/* Main toolbar - single row layout */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search tests..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 h-9"
          />
        </div>

        {/* Controls group */}
        <div className="flex items-center gap-2">
          {/* Filters */}
          <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9">
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {activeFilters.length > 0 && (
                  <Badge variant="secondary" className="ml-2 px-1.5 py-0.5 text-xs">
                    {activeFilters.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0 bg-white" align="start">
              <div className="p-4 border-b border-border">
                <h4 className="font-medium text-sm text-foreground">Filter Tests</h4>
              </div>
              <div className="p-2 space-y-1">
                {filterOptions.map((option) => (
                  <Button
                    key={option.value}
                    variant="ghost"
                    size="sm"
                    className={`w-full justify-start text-left h-8 ${
                      activeFilters.includes(option.value) || (option.value === "all" && activeFilters.length === 0)
                        ? "bg-accent text-accent-foreground"
                        : ""
                    }`}
                    onClick={() => handleFilterToggle(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {/* Column visibility */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9">
                <Columns3 className="h-4 w-4 mr-2" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-white">
              <DropdownMenuLabel>Show/Hide Columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {columnOptions.map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.value}
                  checked={visibleColumns.includes(column.value)}
                  onCheckedChange={() => handleColumnToggle(column.value)}
                  className="text-sm"
                >
                  {column.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Active filters display */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {activeFilters.map((filter) => {
            const option = filterOptions.find(o => o.value === filter);
            return (
              <Badge
                key={filter}
                variant="outline"
                className={`${option?.color || ""} flex items-center gap-1`}
              >
                {option?.label}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-3 w-3 p-0 hover:bg-transparent"
                  onClick={() => handleRemoveFilter(filter)}
                >
                  <X className="h-2 w-2" />
                </Button>
              </Badge>
            );
          })}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAllFilters}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Clear all
          </Button>
        </div>
      )}

      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        Showing {filteredCount.toLocaleString()} of {totalCount.toLocaleString()} tests
      </div>
    </div>
  );
};